import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../shared/lib/supabase';
import SearchableDropdown from '../shared/SearchableDropdown';

interface AddUserOrganisationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UserOrganisationCreateInput) => Promise<void>;
}

interface UserOrganisationCreateInput {
  user_id: string;
  organisation_id: string;
  role: string;
  active: boolean;
}

interface DropdownOption {
  id: string;
  name: string;
  email?: string; // For users
}

const AddUserOrganisationModal: React.FC<AddUserOrganisationModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<UserOrganisationCreateInput>({
    user_id: '',
    organisation_id: '',
    role: '',
    active: true
  });
  
  const [users, setUsers] = useState<DropdownOption[]>([]);
  const [organisations, setOrganisations] = useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch users and organisations for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch ALL users
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name')
          .eq('active', true)
          .order('first_name');

        if (usersError) throw usersError;
        
        // Fetch ALL organisations
        const { data: orgsData, error: orgsError } = await supabase
          .from('organisations')
          .select('id, name')
          .eq('active', true)
          .order('name');

        if (orgsError) throw orgsError;

        setUsers(usersData?.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name} (${user.email})`,
          email: user.email
        })) || []);

        setOrganisations(orgsData?.map(org => ({
          id: org.id,
          name: org.name
        })) || []);

      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};

    if (!formData.user_id) newErrors.user_id = 'User is required';
    if (!formData.organisation_id) newErrors.organisation_id = 'Organization is required';
    if (!formData.role.trim()) newErrors.role = 'Role is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSave(formData);
      onClose();
      // Reset form after successful save
      setFormData({
        user_id: '',
        organisation_id: '',
        role: '',
        active: true
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error adding user organisation:', errorMessage);
      setErrors({ submit: errorMessage });
    }
  };

  const handleInputChange = (field: keyof UserOrganisationCreateInput, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Add User Organization</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User ID - Searchable Dropdown */}
          <SearchableDropdown
            value={formData.user_id}
            onChange={(value) => handleInputChange('user_id', value)}
            options={users}
            placeholder="Select a user..."
            disabled={isLoading}
            label="User *"
            error={errors.user_id}
          />

          {/* Organisation ID - Searchable Dropdown */}
          <SearchableDropdown
            value={formData.organisation_id}
            onChange={(value) => handleInputChange('organisation_id', value)}
            options={organisations}
            placeholder="Select an organization..."
            disabled={isLoading}
            label="Organization *"
            error={errors.organisation_id}
          />

          {/* Role - Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a role...</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            {errors.role && (
              <p className="text-red-500 text-xs mt-1">{errors.role}</p>
            )}
          </div>

          {/* Active - Toggle */}
          <div>
            <label className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active</span>
              <button
                type="button"
                onClick={() => handleInputChange('active', !formData.active)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              Add Data
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserOrganisationModal;
